/**
 * Home view — the Edge Discovery surface (brief §5.1 / D3).
 *
 * Composition per brief §5.1:
 *   1. League filter chips (default "All", chips for each v1 focus
 *      league per §4.1 / D10). Live data from the cron's D10 league
 *      ingestion set — chips outside that set would never have data.
 *   2. Sharpest edge — single hero card (D7: best edge × confidence
 *      per match). Labelled "Sharpest edge" not "Edge of the Night":
 *      Home keeps the full upcoming window, so the hero can be a match
 *      days out — each card shows its kickoff date. Brief D7 hero
 *      concept is unchanged; only the time-of-day copy was dropped.
 *   3. Up-to-N ranked edges below the hero.
 *
 * Tap a row → navigate to Match detail.
 */

import { useEffect, useRef, useState } from 'react';
import type { EdgeCall, EdgeDiscovery, User } from '@sharp8/types';
import { falcon } from '../lib/falcon';
import { haptic } from '../lib/telegram';

interface Props {
  /** edgeId disambiguates when a match has multiple +EV calls (AH and
   *  OU on the same fixture). Without it, Match shows edges[0] which
   *  may not be the call the user just tapped on Home. */
  onOpenMatch(matchId: string, intent?: 'bet' | 'debate', edgeId?: string): void;
  me: { user: User; guest: boolean } | null;
}

/** League filter set — matches the cron's focus list (apps/cron/src/
 *  jobs/ingestApifootball.ts LEAGUES). 'all' is the default; only the
 *  slugs listed here will ever have real fixtures populated. Order
 *  mirrors brief D10 (European-major spine first, then non-European
 *  D10, then additive Asian regional). The chip row scrolls
 *  horizontally on narrow viewports. */
type LeagueSlug =
  | 'all'
  | 'worldcup'
  | 'epl' | 'facup' | 'laliga' | 'bundesliga' | 'seriea' | 'ligue1'
  | 'ucl' | 'uel' | 'spl' | 'mls' | 'aleague'
  | 'afc' | 'jleague' | 'kleague';
const LEAGUE_CHIPS: Array<{ slug: LeagueSlug; label: string }> = [
  { slug: 'all',        label: 'All' },
  { slug: 'worldcup',   label: 'World Cup' },
  { slug: 'epl',        label: 'EPL' },
  { slug: 'facup',      label: 'FA Cup' },
  { slug: 'laliga',     label: 'La Liga' },
  { slug: 'bundesliga', label: 'BUN' },
  { slug: 'seriea',     label: 'SA' },
  { slug: 'ligue1',     label: 'L1' },
  { slug: 'ucl',        label: 'UCL' },
  { slug: 'uel',        label: 'UEL' },
  { slug: 'spl',        label: 'SPL' },
  { slug: 'mls',        label: 'MLS' },
  { slug: 'aleague',    label: 'A-League' },
  { slug: 'afc',        label: 'AFC CL' },
  { slug: 'jleague',    label: 'J-League' },
  { slug: 'kleague',    label: 'K-League' },
];

const PERSONA_AVATARS: Record<string, { bg: string; fg: string; letter: string }> = {
  'sharp8-house': { bg: 'linear-gradient(135deg,#4ED5DB,#2EA8AE)', fg: '#0A1322', letter: 'S' },
  'tanaka':       { bg: 'linear-gradient(135deg,#1A3A6B,#0E1F3D)', fg: '#fff',    letter: 'T' },
  'sofia':        { bg: 'linear-gradient(135deg,#FF6B9D,#C92A6F)', fg: '#fff',    letter: 'S' },
  'arjun':        { bg: 'linear-gradient(135deg,#2D6B47,#1A4A30)', fg: '#fff',    letter: 'A' },
  'caleb':        { bg: 'linear-gradient(135deg,#D4831F,#8B5A0C)', fg: '#fff',    letter: 'C' },
  'lucas':        { bg: 'linear-gradient(135deg,#6B47B8,#422D7A)', fg: '#fff',    letter: 'L' },
};

const PERSONA_ORDER = ['tanaka', 'sofia', 'arjun', 'lucas', 'sharp8-house', 'caleb'] as const;

function fmtTime(iso: string): string {
  const d = new Date(iso);
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  const ss = String(d.getUTCSeconds()).padStart(2, '0');
  return `${hh}:${mm}:${ss} UTC`;
}

/** Kickoff date+time in the viewer's local timezone (SEA punters are
 *  UTC+8) with the offset shown, so a match days out can't be mistaken
 *  for one playing now. Falls back to a plain string if Intl chokes. */
function fmtKickoff(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return d.toLocaleString(undefined, {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
      hour12: false, timeZoneName: 'short',
    });
  } catch {
    return d.toISOString();
  }
}

function capPersona(s: string): string {
  if (s === 'sharp8-house') return 'Sharp8 House';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function Home({ onOpenMatch, me }: Props) {
  const [league, setLeague] = useState<LeagueSlug>('all');
  const [data, setData] = useState<EdgeDiscovery | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    setError(null);
    const opts: { limit: number; league?: string } = { limit: 4 };
    if (league !== 'all') opts.league = league;
    falcon.edges.today(opts)
      .then((d) => { if (!cancelled) { setData(d); setLoaded(true); } })
      .catch((e) => { if (!cancelled) { setError(String(e?.message ?? e)); setLoaded(true); } });
    return () => { cancelled = true; };
  }, [league]);

  if (error) {
    return (
      <div style={{ padding: '80px 32px', textAlign: 'center' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--negative)' }}>Couldn't load edges</div>
        <div style={{ fontSize: 13, color: 'var(--ink-soft)', marginTop: 8 }}>{error}</div>
      </div>
    );
  }

  // Distinguish three states:
  //   not yet loaded         → SkeletonHero
  //   loaded, no edges       → empty-state card (silence is a feature per Brief Rule 2)
  //   loaded, edges present  → EdgeHeroCard
  const isEmpty = loaded && !data?.hero;

  // "See all N" target — when there are more edges below the hero,
  // tapping the link smooth-scrolls to the secondary edge list. Brief
  // §5.1 keeps Home as the canonical Edge Discovery surface; the rest
  // are already on the page, just below the fold on small viewports.
  const moreRef = useRef<HTMLDivElement>(null);
  const handleSeeAll = () => {
    haptic('light');
    moreRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      {me && !me.guest && <BankrollPill bankroll={me.user.bankroll} />}
      <LeagueChips selected={league} onSelect={setLeague} />
      <SectionHead
        title="Sharpest edge"
        more={data?.more.length ? `See all ${1 + data.more.length} →` : undefined}
        onMore={data?.more.length ? handleSeeAll : undefined}
      />
      {!loaded ? (
        <SkeletonHero />
      ) : data?.hero ? (
        <EdgeHeroCard
          edge={data.hero}
          onOpen={(intent) => onOpenMatch(data.hero.matchId, intent, data.hero.id)}
        />
      ) : (
        <NoEdgesCard />
      )}

      {/* Hide the entire "More edges" block when the hero is also
       *  empty — the NoEdgesCard already speaks for the silence. When
       *  hero exists but more is empty, show the section with an
       *  explicit empty message (NOT skeleton rows — skeletons mean
       *  loading, not "nothing to show"). */}
      {!loaded ? (
        <div ref={moreRef}>
          <SectionHead title="More edges" />
          <div className="edge-list">
            <SkeletonRow count={3} />
          </div>
        </div>
      ) : data?.more.length ? (
        <div ref={moreRef}>
          <SectionHead title="More edges" />
          <div className="edge-list">
            {data.more.map((edge) => (
              <EdgeRow key={edge.id} edge={edge} onOpen={() => onOpenMatch(edge.matchId, undefined, edge.id)} />
            ))}
          </div>
        </div>
      ) : data?.hero ? (
        <div ref={moreRef}>
          <SectionHead title="More edges" />
          <div className="edge-list">
            <p className="edge-list__empty">
              No other edges right now. The engine sits out when no other bookmaker line is mispriced.
            </p>
          </div>
        </div>
      ) : null}

      <p className="footer-note">
        Sharp8 is an edge identifier, not a tipster.<br />
        The panel exposes reasoning, not competing picks.
      </p>
    </>
  );
}

// ── Sub-components ──────────────────────────────────────────────────

function LeagueChips({ selected, onSelect }: { selected: LeagueSlug; onSelect: (s: LeagueSlug) => void }) {
  return (
    <nav className="league-chips" aria-label="Filter by league">
      {LEAGUE_CHIPS.map((chip) => (
        <button
          key={chip.slug}
          type="button"
          className={`league-chip${selected === chip.slug ? ' is-active' : ''}`}
          onClick={() => { haptic('light'); onSelect(chip.slug); }}
          aria-pressed={selected === chip.slug}
        >
          {chip.label}
        </button>
      ))}
    </nav>
  );
}

function BankrollPill({ bankroll }: { bankroll: number }) {
  // Tag the dollar prefix with a "GAME" badge so it never reads as real
  // money — brief §6.2 anti-scam discipline. Sharp8 has no real-money
  // rails (§4.4); this is F2P contest accounting only.
  const diff = bankroll - 1000;
  const diffSign = diff >= 0 ? '+' : '−';
  const diffAbs  = Math.abs(diff);
  const diffCls  = diff >= 0 ? 'green' : 'red';
  return (
    <section className="bankroll-pill" aria-label="Game bankroll">
      <span className="bankroll-pill__tag">GAME</span>
      <span className="bankroll-pill__amount">
        ${bankroll.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </span>
      <span className={`bankroll-pill__delta ${diffCls}`}>
        {diff === 0 ? 'at start' : `${diffSign}$${diffAbs.toFixed(2)} from $1,000`}
      </span>
    </section>
  );
}

function SectionHead({ title, more, onMore }: { title: string; more?: string; onMore?: () => void }) {
  return (
    <div className="section-head">
      <span className="section-head__title">
        <span className="brand-dot" />
        {title}
      </span>
      {more && (
        onMore
          ? <button type="button" className="section-head__more section-head__more--button" onClick={onMore}>{more}</button>
          : <span className="section-head__more">{more}</span>
      )}
    </div>
  );
}

function Crest({ team, fallbackColor, fallbackInitial }: { team: { crestUrl: string; name: string }; fallbackColor: string; fallbackInitial: string }) {
  return (
    <img
      src={team.crestUrl}
      alt={team.name}
      style={{ width: '82%', height: '82%', objectFit: 'contain' }}
      onError={(e) => {
        const img = e.currentTarget;
        const parent = img.parentElement;
        if (parent) {
          parent.style.background = fallbackColor;
          parent.innerHTML = `<span style="color:#fff;font-weight:800;font-size:14px">${fallbackInitial}</span>`;
        }
      }}
    />
  );
}

function EdgeHeroCard({ edge, onOpen }: { edge: EdgeCall; onOpen: (intent?: 'bet' | 'debate') => void }) {
  // Match data is on the EdgeCall via matchId — need to fetch the match
  // for league + team logos. Lightweight: SDK call.
  const [match, setMatch] = useState<{ home: any; away: any; league: any; state: string; score: any; kickoff: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    falcon.match.get(edge.matchId)
      .then((m) => { if (!cancelled) setMatch(m); })
      .catch(() => { /* silent — card still renders without team data */ });
    return () => { cancelled = true; };
  }, [edge.matchId]);

  if (!match) return <SkeletonHero />;

  const isLive = match.state === 'live';
  const firstDissent = edge.panel.dissent[0];

  return (
    <article className="edge-hero" onClick={onOpen} style={{ cursor: 'pointer' }}>
      <div className="edge-hero__corner" />

      <header className="edge-hero__matchup">
        <div className="edge-hero__league">
          <span className="edge-hero__league-name">{match.league.name}</span>
          {isLive && match.score ? (
            <span className="edge-hero__league-state">LIVE · {match.score.minute}</span>
          ) : (
            match.kickoff && (
              <span className="edge-hero__kickoff" style={{ color: 'var(--ink-soft)', fontWeight: 600, fontSize: 12 }}>
                {fmtKickoff(match.kickoff)}
              </span>
            )
          )}
        </div>

        <div className="edge-hero__teams">
          <div className="edge-hero__team">
            <span className="edge-hero__crest"><Crest team={match.home} fallbackColor={match.home.primaryColor} fallbackInitial={match.home.shortName[0]} /></span>
            <span className="edge-hero__team-name">{match.home.shortName}</span>
          </div>

          {isLive && match.score ? (
            <div className="edge-hero__score">
              <div className="edge-hero__score-nums">{match.score.home} – {match.score.away}</div>
              <div className="edge-hero__score-min">{match.score.minute}</div>
            </div>
          ) : (
            <div className="edge-hero__vs">vs</div>
          )}

          <div className="edge-hero__team edge-hero__team--away">
            <span className="edge-hero__crest"><Crest team={match.away} fallbackColor={match.away.primaryColor} fallbackInitial={match.away.shortName[0]} /></span>
            <span className="edge-hero__team-name">{match.away.shortName}</span>
          </div>
        </div>
      </header>

      <div className="edge-hero__call">
        <span className="edge-hero__verified">{fmtTime(edge.verifiedAt)}</span>
        <div className="edge-hero__verdict">EDGE CALL</div>
        <div className="edge-hero__line">
          Take <b>{edge.line}</b>{' '}
          @ <span className="price">{edge.price.toFixed(2)}</span>
          <span className="book">· {edge.book}</span>
        </div>
        <div className="edge-hero__freshness">
          <span className="dot" />
          {edge.freshness.toUpperCase()}
          <span className="delta">+{(edge.ev * 100).toFixed(0)}% EV · price held</span>
        </div>
      </div>

      <div className="edge-hero__reasoning">
        <div className="edge-hero__reasoning-label">Synthesis</div>
        <div className="edge-hero__reasoning-body">
          {edge.reasoning.body}
          {(() => {
            const c = edge.reasoning.citations[0];
            return c ? (
              <a className="edge-hero__reasoning-cite">{c.source} · {fmtTime(c.verifiedAt)}</a>
            ) : null;
          })()}
        </div>
      </div>

      <div className="edge-hero__panel">
        <div className="edge-hero__panel-avatars">
          {PERSONA_ORDER.map((key) => {
            const av = PERSONA_AVATARS[key];
            if (!av) return null;
            return (
              <span key={key} className="edge-hero__panel-avatar" style={{ background: av.bg, color: av.fg }}>
                {av.letter}
              </span>
            );
          })}
        </div>
        <div className="edge-hero__panel-text">
          <b>{edge.panel.backing} of {edge.panel.total} back this call.</b>{' '}
          {firstDissent && (
            <>
              <span className="dissent">{capPersona(firstDissent.personaKey)} dissents</span> — {firstDissent.reasoning}
            </>
          )}
        </div>
      </div>

      <div className="edge-hero__ctas">
        <button
          className="edge-hero__cta edge-hero__cta--primary"
          onClick={(e) => { e.stopPropagation(); haptic('medium'); onOpen('bet'); }}
        >
          Place bet · {edge.price.toFixed(2)}
        </button>
        <button
          className="edge-hero__cta edge-hero__cta--secondary"
          onClick={(e) => { e.stopPropagation(); haptic('light'); onOpen('debate'); }}
        >
          Debate
        </button>
      </div>
    </article>
  );
}

function EdgeRow({ edge, onOpen }: { edge: EdgeCall; onOpen: () => void }) {
  const [match, setMatch] = useState<{ home: any; away: any; league: any; kickoff: string } | null>(null);
  useEffect(() => {
    let cancelled = false;
    falcon.match.get(edge.matchId).then((m) => { if (!cancelled) setMatch(m); }).catch(() => {});
    return () => { cancelled = true; };
  }, [edge.matchId]);
  if (!match) return <SkeletonRow count={1} />;

  return (
    <a className="edge-row" onClick={() => { haptic('light'); onOpen(); }}>
      <div className="edge-row__logos">
        <span className="edge-row__crest edge-row__crest--home"><Crest team={match.home} fallbackColor={match.home.primaryColor} fallbackInitial={match.home.shortName[0]} /></span>
        <span className="edge-row__crest edge-row__crest--away"><Crest team={match.away} fallbackColor={match.away.primaryColor} fallbackInitial={match.away.shortName[0]} /></span>
      </div>
      <div className="edge-row__info">
        <div className="edge-row__teams">{match.home.shortName} vs {match.away.shortName}</div>
        <div className="edge-row__call">
          Take {edge.line} @ <span className="price">{edge.price.toFixed(2)}</span>{' '}
          <span className="book">· {edge.book}</span>
        </div>
        <div className="edge-row__meta">
          {match.league.shortName}
          {match.kickoff && <> · {fmtKickoff(match.kickoff)}</>}
          {' '}· +{(edge.ev * 100).toFixed(0)}% EV
        </div>
      </div>
      <span className={`edge-row__freshness${edge.freshness === 'tightened' ? ' edge-row__freshness--watch' : ''}`}>
        <span className="dot" />
        {edge.freshness === 'tightened' ? 'WATCH' : 'ALIVE'}
      </span>
    </a>
  );
}

// ── Skeleton placeholders ───────────────────────────────────────────

function SkeletonHero() {
  return (
    <div style={{
      margin: '0 16px',
      padding: 60,
      background: 'var(--surface)',
      borderRadius: 20,
      border: '1px solid var(--hairline)',
      textAlign: 'center',
      color: 'var(--ink-mute)',
      fontSize: 13,
    }}>
      Loading edges…
    </div>
  );
}

function SkeletonRow({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} style={{
          height: 76,
          background: 'var(--surface)',
          borderRadius: 16,
          border: '1px solid var(--hairline)',
          opacity: 0.6,
        }} />
      ))}
    </>
  );
}

// ── No-edges empty state (Brief Rule 2 · "silence is a feature") ────

function NoEdgesCard() {
  return (
    <div style={{
      margin: '0 16px',
      padding: '40px 28px',
      background: 'var(--surface)',
      borderRadius: 20,
      border: '1px dashed var(--hairline-strong)',
      textAlign: 'center',
    }}>
      <div style={{
        fontFamily: 'var(--font-display, var(--font-sans))',
        fontSize: 20,
        fontWeight: 700,
        letterSpacing: '-0.02em',
        color: 'var(--ink)',
        marginBottom: 8,
      }}>
        No edges right now
      </div>
      <div style={{
        fontSize: 13.5,
        lineHeight: 1.55,
        color: 'var(--ink-soft)',
        letterSpacing: '-0.005em',
      }}>
        Sharp8 calls edges only when the market is wrong.
        Silence is a feature, not a regression.
        Check back later — the engine scans every 10 minutes.
      </div>
    </div>
  );
}
